/**
 * PositionList component with dark theme styling and pagination.
 */

import { useState, useMemo, useCallback } from 'react';
import { Table, Input, Spinner, Card, CardContent, Pagination } from '@/components/ui';
import type { Column, SortDirection } from '@/components/ui';
import { formatCurrency, formatNumber } from '@/utils/formatters';
import type { Position } from '@/types';

interface PositionListProps {
  positions: Position[];
  isLoading?: boolean;
  error?: string | null;
  /** Current page number */
  currentPage?: number;
  /** Whether there is a next page */
  hasNextPage?: boolean;
  /** Whether there is a previous page */
  hasPrevPage?: boolean;
  /** Go to next page callback */
  onNextPage?: () => void;
  /** Go to previous page callback */
  onPrevPage?: () => void;
  /** Go to first page callback */
  onFirstPage?: () => void;
  /** Page size */
  pageSize?: number;
}

type SortKey = 'symbol' | 'quantity' | 'average_cost' | 'cost_basis' | 'realized_pnl';

export function PositionList({
  positions,
  isLoading = false,
  error = null,
  currentPage,
  hasNextPage = false,
  hasPrevPage = false,
  onNextPage,
  onPrevPage,
  onFirstPage,
  pageSize,
}: PositionListProps) {
  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('symbol');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = useCallback((key: string) => {
    if (key === sortKey) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key as SortKey);
      setSortDirection('asc');
    }
  }, [sortKey]);

  const filteredAndSorted = useMemo(() => {
    let result = [...positions];

    if (filter) {
      const lowerFilter = filter.toLowerCase();
      result = result.filter((p) => p.symbol.toLowerCase().includes(lowerFilter));
    }

    result.sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortKey) {
        case 'symbol':
          aVal = a.symbol;
          bVal = b.symbol;
          break;
        case 'quantity':
          aVal = a.quantity;
          bVal = b.quantity;
          break;
        case 'average_cost':
          aVal = a.average_cost;
          bVal = b.average_cost;
          break;
        case 'cost_basis':
          aVal = a.cost_basis;
          bVal = b.cost_basis;
          break;
        case 'realized_pnl':
          aVal = a.realized_pnl;
          bVal = b.realized_pnl;
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortDirection === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });

    return result;
  }, [positions, filter, sortKey, sortDirection]);

  const columns: Column<Position>[] = useMemo(
    () => [
      {
        key: 'symbol',
        header: 'Symbol',
        sortable: true,
        render: (position) => (
          <span className="font-medium text-dark-100">{position.symbol}</span>
        ),
      },
      {
        key: 'quantity',
        header: 'Quantity',
        sortable: true,
        render: (position) => (
          <span className={`font-mono ${position.quantity < 0 ? 'text-rose-400' : 'text-dark-200'}`}>
            {formatNumber(position.quantity)}
          </span>
        ),
      },
      {
        key: 'average_cost',
        header: 'Entry Price',
        sortable: true,
        render: (position) => (
          <span className="font-mono text-dark-200">{formatCurrency(position.average_cost)}</span>
        ),
      },
      {
        key: 'cost_basis',
        header: 'Cost Basis',
        sortable: true,
        render: (position) => (
          <span className="font-mono text-dark-200">{formatCurrency(position.cost_basis)}</span>
        ),
      },
      {
        key: 'realized_pnl',
        header: 'Realized P&L',
        sortable: true,
        render: (position) => {
          const colorClass = position.realized_pnl > 0
            ? 'text-emerald-400'
            : position.realized_pnl < 0
              ? 'text-rose-400'
              : 'text-dark-200';
          return (
            <span className={`font-mono font-medium ${colorClass}`}>
              {formatCurrency(position.realized_pnl)}
            </span>
          );
        },
      },
    ],
    []
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Spinner size="lg" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-rose-400">{error}</p>
        </CardContent>
      </Card>
    );
  }

  const showPagination = currentPage !== undefined && onNextPage && onPrevPage;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-dark-100">
          Positions <span className="text-dark-400 font-normal">({positions.length})</span>
        </h2>
        <div className="w-64">
          <Input
            placeholder="Filter by symbol..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            aria-label="Filter positions by symbol"
          />
        </div>
      </div>
      <Table
        data={filteredAndSorted}
        columns={columns}
        keyExtractor={(position) => position.position_id}
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSort={handleSort}
        emptyMessage="No positions found"
      />
      {showPagination && (
        <Pagination
          currentPage={currentPage}
          hasNextPage={hasNextPage}
          hasPrevPage={hasPrevPage}
          onNextPage={onNextPage}
          onPrevPage={onPrevPage}
          onFirstPage={onFirstPage}
          pageItemCount={filteredAndSorted.length}
          pageSize={pageSize}
        />
      )}
    </div>
  );
}
